import {
  getPendingFinalSubmissions,
  updateFinalSubmission,
  deleteFinalSubmission,
  clearScreenDrafts,
  getDb
} from "./countDraftDb";
import { normalizePeriodKey } from "./countDraftConstants";
import logger from "../utils/logger";
import { getFriendlyErrorMessage } from "../utils/errorMessages";

let processing = false;
let bound = false;
const listeners = new Set();

export function subscribeFinalSubmissionSync(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function notifyFinalSubmissionListeners(event) {
  listeners.forEach((fn) => {
    try {
      fn(event);
    } catch (_) {
      /* ignore */
    }
  });
}

function classifyFinalSubmitError(err) {
  const status = err.response && err.response.status;
  const data = err.response && err.response.data;
  if (!err.response) {
    return {
      kind: "network",
      retriable: true,
      message: getFriendlyErrorMessage(err, { kind: "final_submit_pending" })
    };
  }
  if (data && data.code === "STALE_COUNTING_PERIOD") {
    return {
      kind: "stale_period",
      retriable: false,
      message: getFriendlyErrorMessage(err, { kind: "final_submit" })
    };
  }
  if (status === 400 || status === 409) {
    // Active batch / validation — treat as terminal (do not keep retrying forever).
    return {
      kind: "rejected",
      retriable: false,
      message: getFriendlyErrorMessage(err, { kind: "final_submit_failed" })
    };
  }
  if (status === 401 || status === 403) {
    return {
      kind: "auth",
      retriable: false,
      message: getFriendlyErrorMessage(err, { kind: "final_submit_failed" })
    };
  }
  if (status >= 500) {
    return {
      kind: "server",
      retriable: true,
      message: getFriendlyErrorMessage(err, { kind: "final_submit_pending" })
    };
  }
  return {
    kind: "other",
    retriable: false,
    message: getFriendlyErrorMessage(err, { kind: "final_submit_failed" })
  };
}

export function bindFinalSubmissionConnectivity(runSync) {
  if (typeof window === "undefined" || bound) return;
  bound = true;
  window.addEventListener("online", () => {
    runSync();
  });
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") runSync();
  });
  window.addEventListener("focus", () => {
    runSync();
  });
}

/**
 * Persist a final submission locally, then attempt POST. Network failures leave status pending.
 */
export async function saveAndAttemptFinalSubmission(axiosInstance, record, options) {
  const db = await getDb();
  if (!db || !db.finalSubmissions) {
    throw new Error("Local storage unavailable for pending submission");
  }
  const now = new Date().toISOString();
  const row = {
    ...record,
    status: "pending",
    createdAt: record.createdAt || now,
    lastAttemptAt: null,
    attemptCount: record.attemptCount || 0,
    lastError: null,
    serverBatchId: null,
    pendingConfirmedAt: record.pendingConfirmedAt || null
  };
  await db.finalSubmissions.put(row);
  notifyFinalSubmissionListeners({ type: "queued", row });

  const online = typeof navigator === "undefined" || navigator.onLine;
  if (!online) {
    const pendingRow = {
      ...row,
      pendingConfirmedAt: new Date().toISOString(),
      lastError: "Waiting for a network connection"
    };
    await updateFinalSubmission(row.id, {
      pendingConfirmedAt: pendingRow.pendingConfirmedAt,
      lastError: pendingRow.lastError
    }, row.clientSubmissionId);
    notifyFinalSubmissionListeners({ type: "pending", row: pendingRow });
    return { outcome: "pending", row: pendingRow };
  }

  return attemptFinalSubmissionRow(axiosInstance, row, options);
}

export async function attemptFinalSubmissionRow(axiosInstance, row, options) {
  const onProgress = options && options.onProgress;
  const nextAttempt = (row.attemptCount || 0) + 1;
  await updateFinalSubmission(row.id, {
    status: "syncing",
    lastAttemptAt: new Date().toISOString(),
    attemptCount: nextAttempt
  }, row.clientSubmissionId);
  notifyFinalSubmissionListeners({ type: "syncing", id: row.id });
  if (onProgress) onProgress({ type: "syncing", row });

  const body = {
    clientSubmissionId: row.clientSubmissionId,
    currentPeriodKey: row.currentPeriodKey,
    countingPeriodStartedAt: row.countingPeriodStartedAt,
    personName: row.payload.personName,
    generalNotes: row.payload.generalNotes,
    recalledBatchId: row.payload.recalledBatchId || null,
    counts: row.payload.counts
  };

  try {
    const res = await axiosInstance.post("/api/tenant/site/counts", body);
    const batchId = res.data && res.data.batchId;
    await updateFinalSubmission(row.id, {
      status: "submitted",
      serverBatchId: batchId || null,
      lastError: null,
      attemptCount: nextAttempt
    }, row.clientSubmissionId);
    try {
      await clearScreenDrafts(row.tenantId, row.userId, row.siteId, row.currentPeriodKey);
    } catch (e) {
      logger.warn("[finalSubmissionSync] clear drafts after submit", e && e.message);
    }
    await deleteFinalSubmission(row.id, row.clientSubmissionId);
    const done = { ...row, status: "submitted", serverBatchId: batchId || null, attemptCount: nextAttempt };
    notifyFinalSubmissionListeners({ type: "submitted", row: done, response: res.data });
    if (onProgress) onProgress({ type: "submitted", row: done, response: res.data });
    return { outcome: "submitted", row: done, response: res.data };
  } catch (err) {
    const classified = classifyFinalSubmitError(err);
    if (classified.kind === "stale_period") {
      await updateFinalSubmission(row.id, {
        status: "stale",
        lastError: classified.message,
        attemptCount: nextAttempt
      }, row.clientSubmissionId);
      notifyFinalSubmissionListeners({ type: "stale", id: row.id, message: classified.message });
      if (onProgress) onProgress({ type: "stale", message: classified.message });
      return { outcome: "stale", message: classified.message };
    }
    if (!classified.retriable) {
      await updateFinalSubmission(row.id, {
        status: "failed",
        lastError: classified.message,
        attemptCount: nextAttempt
      }, row.clientSubmissionId);
      notifyFinalSubmissionListeners({ type: "failed", id: row.id, message: classified.message });
      if (onProgress) onProgress({ type: "failed", message: classified.message });
      return { outcome: "failed", message: classified.message };
    }
    const backoffMs = Math.min(120000, 3000 * Math.pow(2, Math.min(nextAttempt, 6)));
    const pendingConfirmedAt = new Date().toISOString();
    const nextRetryAt = new Date(Date.now() + backoffMs).toISOString();
    await updateFinalSubmission(row.id, {
      status: "pending",
      lastError: classified.message,
      attemptCount: nextAttempt,
      nextRetryAt,
      pendingConfirmedAt
    }, row.clientSubmissionId);
    const pendingRow = {
      ...row,
      status: "pending",
      lastError: classified.message,
      attemptCount: nextAttempt,
      nextRetryAt,
      pendingConfirmedAt
    };
    notifyFinalSubmissionListeners({ type: "pending", row: pendingRow, message: classified.message });
    if (onProgress) onProgress({ type: "pending", message: classified.message });
    return { outcome: "pending", row: pendingRow, message: classified.message };
  }
}

export async function processFinalSubmissionQueue(axiosInstance, scope, options) {
  if (processing) return;
  const tenantId = scope && scope.tenantId;
  const userId = scope && scope.userId;
  if (!tenantId || !userId) return;
  if (typeof navigator !== "undefined" && !navigator.onLine) return;

  const db = await getDb();
  if (!db || !db.finalSubmissions) return;

  processing = true;
  try {
    const pending = await getPendingFinalSubmissions(tenantId, userId);
    const now = Date.now();
    const due = pending.filter((r) => {
      if (r.nextRetryAt && new Date(r.nextRetryAt).getTime() > now) return false;
      return r.status === "pending" || r.status === "syncing";
    });
    // Prefer current site/period first when provided
    due.sort((a, b) => {
      const aMatch =
        scope.siteId &&
        scope.currentPeriodKey != null &&
        a.siteId === scope.siteId &&
        a.currentPeriodKey === scope.currentPeriodKey
          ? 0
          : 1;
      const bMatch =
        scope.siteId &&
        scope.currentPeriodKey != null &&
        b.siteId === scope.siteId &&
        b.currentPeriodKey === scope.currentPeriodKey
          ? 0
          : 1;
      if (aMatch !== bMatch) return aMatch - bMatch;
      return new Date(a.createdAt) - new Date(b.createdAt);
    });

    for (const row of due) {
      // Skip stale-period rows for a different period than active screen (still try; server returns 409).
      if (
        scope.currentPeriodKey != null &&
        row.siteId === scope.siteId &&
        normalizePeriodKey(row.currentPeriodKey) !== normalizePeriodKey(scope.currentPeriodKey)
      ) {
        await updateFinalSubmission(row.id, {
          status: "stale",
          lastError: "Counting period has changed"
        }, row.clientSubmissionId);
        notifyFinalSubmissionListeners({ type: "stale", id: row.id });
        continue;
      }
      await attemptFinalSubmissionRow(axiosInstance, row, options);
    }
  } finally {
    processing = false;
  }
}
